import { isWatchlistGroupingAttribute } from '../utils/watchlistAttributePresentation';

export const UNASSIGNED_GROUP_KIND = 'watchlist_optional_metadata_missing';
export const UNASSIGNED_GROUP_VALUE = Object.freeze({ kind: UNASSIGNED_GROUP_KIND });
export const UNASSIGNED_GROUP_LABEL = 'Unassigned';
export const UNKNOWN_VALUE = 'unknown';
export const GENERIC_ATTRIBUTE_GROUP_KINDS = Object.freeze({
    ASSIGNED: 'assigned',
    UNASSIGNED: 'unassigned',
});
export const STALE_ATTRIBUTE_SCOPE = 'stale_attribute';

export function isUnassignedGroupValue(value) {
    return Boolean(value && typeof value === 'object' && value.kind === UNASSIGNED_GROUP_KIND);
}

export function isMissingOptionalGroupInput(value) {
    if (isUnassignedGroupValue(value)) return true;
    if (value == null) return true;
    const text = String(value).trim();
    return !text || text.toLowerCase() === UNKNOWN_VALUE;
}

export function normalizeGroupValueForUi(value) {
    if (isMissingOptionalGroupInput(value)) {
        return { kind: UNASSIGNED_GROUP_KIND };
    }
    return String(value).trim();
}

export function displayGroupValue(value) {
    if (isMissingOptionalGroupInput(value)) {
        return UNASSIGNED_GROUP_LABEL;
    }
    return String(value ?? '').trim();
}

export function groupValueKey(value) {
    if (isMissingOptionalGroupInput(value)) {
        return `missing:${UNASSIGNED_GROUP_KIND}`;
    }
    return `assigned:${String(value ?? '').trim()}`;
}

export function groupValueSortKey(value) {
    if (isMissingOptionalGroupInput(value)) {
        return `zz:${UNASSIGNED_GROUP_LABEL}`;
    }
    return `aa:${String(value ?? '').trim().toLowerCase()}`;
}

export function genericAttributeGroupIdentity(group) {
    if (!group || typeof group !== 'object') return '';
    const definitionId = String(group.attribute_definition_id || '').trim();
    const kind = String(group.group_kind || '').trim();
    if (!definitionId || !Object.values(GENERIC_ATTRIBUTE_GROUP_KINDS).includes(kind)) {
        return '';
    }
    const valueKey = kind === GENERIC_ATTRIBUTE_GROUP_KINDS.UNASSIGNED
        ? ''
        : String(group.group_value_key || '').trim();
    return `${definitionId}:${kind}:${valueKey}`;
}

export function displayGenericAttributeGroup(group) {
    return group?.group_kind === GENERIC_ATTRIBUTE_GROUP_KINDS.UNASSIGNED
        ? UNASSIGNED_GROUP_LABEL
        : String(group?.group_value || '').trim();
}

export function genericAttributeGroupSortKey(group) {
    return group?.group_kind === GENERIC_ATTRIBUTE_GROUP_KINDS.UNASSIGNED
        ? `zz:${UNASSIGNED_GROUP_LABEL.toLowerCase()}`
        : `aa:${String(group?.group_value || '').trim().toLowerCase()}`;
}

export function buildGenericAttributeMeta(snapshot, watchlistInfoRows, includeUnassigned = false, analysisGroups = null) {
    const definitions = Array.isArray(snapshot?.definitions)
        ? snapshot.definitions.filter(isWatchlistGroupingAttribute)
        : [];
    const rows = Array.isArray(watchlistInfoRows) ? watchlistInfoRows : [];
    const projectedByIdentity = new Map();
    (Array.isArray(snapshot?.values) ? snapshot.values : []).forEach((value) => {
        const cogniveId = String(value?.cognive_id || '').trim();
        const definitionId = String(value?.attribute_definition_id || '').trim();
        if (!cogniveId || !definitionId) return;
        projectedByIdentity.set(`${cogniveId}:${definitionId}`, {
            attribute_definition_id: definitionId,
            group_kind: GENERIC_ATTRIBUTE_GROUP_KINDS.ASSIGNED,
            group_value: value.value_text,
            group_value_key: value.value_key,
        });
    });
    const context = {
        watchlistRevisionId: String(snapshot?.revision_id || ''),
        attributeDefinitionFingerprint: String(snapshot?.definition_fingerprint || ''),
        attributeContentFingerprint: String(snapshot?.content_fingerprint || ''),
    };
    return definitions
        .map((definition) => {
            const definitionId = String(definition?.attribute_definition_id || '').trim();
            if (!definitionId) return null;
            const groups = new Map();
            // Historical returns already contain the exact accepted partition.
            // Optional company-label reads must not replace that inventory.
            if (Array.isArray(analysisGroups)) {
                const partition = analysisGroups.find((item) => item.attribute_definition_id === definitionId);
                (partition?.groups || []).forEach(({ group }) => {
                    if (group?.attribute_definition_id !== definitionId || !Object.values(GENERIC_ATTRIBUTE_GROUP_KINDS).includes(group.group_kind)) return;
                    if (!includeUnassigned && group.group_kind === GENERIC_ATTRIBUTE_GROUP_KINDS.UNASSIGNED) return;
                    groups.set(genericAttributeGroupIdentity(group), group);
                });
            } else rows.forEach((row) => {
                const cogniveId = String(row?.cognive_id || '').trim();
                const group = projectedByIdentity.get(`${cogniveId}:${definitionId}`) || (includeUnassigned ? { attribute_definition_id: definitionId, group_kind: GENERIC_ATTRIBUTE_GROUP_KINDS.UNASSIGNED, group_value: null, group_value_key: null } : null);
                if (!group) return;
                groups.set(genericAttributeGroupIdentity(group), group);
            });
            return {
                key: definitionId,
                definitionId,
                title: String(definition?.display_name || '').trim(),
                systemKey: String(definition?.system_key || '').trim(),
                displayOrder: Number(definition?.display_order || 0),
                values: Array.from(groups.values()).sort((a, b) => (
                    genericAttributeGroupSortKey(a).localeCompare(genericAttributeGroupSortKey(b))
                )),
                ...context,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.displayOrder - b.displayOrder);
}

export function genericPerformanceCard(meta, group) {
    return {
        scope: 'attribute',
        attributeDefinitionId: meta.definitionId,
        attributeLabel: meta.title,
        attributeGroup: group,
        watchlistRevisionId: meta.watchlistRevisionId,
        attributeDefinitionFingerprint: meta.attributeDefinitionFingerprint,
        attributeContentFingerprint: meta.attributeContentFingerprint,
    };
}

export function genericPerformanceSelectionIdentity(card) {
    if (card?.scope === 'portfolio') return 'portfolio';
    if (card?.scope === STALE_ATTRIBUTE_SCOPE) {
        return `stale:${String(card?.staleId || '')}`;
    }
    if (card?.scope !== 'attribute') return '';
    return JSON.stringify({
        scope: 'attribute',
        group: genericAttributeGroupIdentity(card.attributeGroup),
    });
}

function staleCard(card, reason, index) {
    return {
        scope: STALE_ATTRIBUTE_SCOPE,
        staleId: `${index}:${JSON.stringify(card)}`,
        staleReason: reason,
        legacyCard: card,
        requiresUserAction: true,
    };
}

function savedCardPreferences(card) {
    return Object.fromEntries(
        ['lookback', 'decompositionMode', 'dayMode', 'bucketFrequency']
            .filter((key) => card?.[key] !== undefined)
            .map((key) => [key, card[key]])
    );
}

export function migrateSavedPerformanceCards(savedCards, snapshot, watchlistInfoRows, includeUnassigned = false, analysisGroups = null) {
    const cards = Array.isArray(savedCards) ? savedCards : [];
    const meta = buildGenericAttributeMeta(snapshot, watchlistInfoRows, includeUnassigned, analysisGroups);
    const metaById = new Map(meta.map((item) => [item.definitionId, item]));
    return cards.map((card, index) => {
        if (card?.scope === 'portfolio') return { scope: 'portfolio' };
        const matchedMeta = metaById.get(String(card?.attributeDefinitionId || '').trim());
        if (!matchedMeta) return staleCard(card, 'definition_retired', index);
        const groupIdentity = genericAttributeGroupIdentity(card?.attributeGroup);
        const matchedGroup = matchedMeta.values.find(
            (group) => genericAttributeGroupIdentity(group) === groupIdentity
        );
        if (!matchedGroup) return staleCard(card, 'group_retired', index);
        return {
            ...genericPerformanceCard(matchedMeta, matchedGroup),
            ...savedCardPreferences(card),
        };
    });
}

export function reconcileSavedPerformanceCards(savedCards, snapshot, watchlistInfoRows, includeUnassigned = false, analysisGroups = null) {
    return migrateSavedPerformanceCards(savedCards, snapshot, watchlistInfoRows, includeUnassigned, analysisGroups)
        .filter((card) => card.scope !== STALE_ATTRIBUTE_SCOPE);
}
